/* eslint-disable jsx-a11y/anchor-is-valid */
import { Dropdown, message, Input, Spin, Tooltip, Avatar as AntAvatar, type MenuProps } from "antd";
import { Crm } from "../../icons/crm";

import "./style.scss";
import Api from "../../Network";
import { requestManager } from "../../utils/requestManager";
import { LogoutIcon } from "../../icons/logoutIcon";
import { useSelector } from "react-redux";
import { ImageWithChecker } from "../ImageChecker";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../Hooks/useTheme";
import { useState, useEffect, useRef, useCallback } from "react";
import FullScreenIcon from "../../icons/FullScreenIcon";
import { DarkMode } from "../../icons/DarkModeIcons/DarkMode";
import { LightMode } from "../../icons/DarkModeIcons/lightMode";
import { queryKeys } from "../../Utilis/queryKeys";
import { deleteQueryPresister } from "../../Utilis/createQueryPresister";
import { useResponsive, useMobileMenu } from "../../Hooks/useContext";
import { usePermissions } from "../../Hooks/usePermissions";
import { ReloadOutlined, SearchOutlined, MenuOutlined } from "@ant-design/icons";
import { isNewTabClick, useOpenRoute, useRouteLink } from "../../Hooks/useRouteLink";
import { useHomeShortcuts } from "../../Hooks/useHomeShortcuts";
import { UserIcon } from "../../icons/UserIcon";
import { NotificationCenter } from "../NotificationCenter";

const CHATBOT_URL = "https://try.awfar.io/";

interface Customer {
  id: number;
  name: string;
  phone?: string;
}

export const Header = () => {
  const { userInfo, company } = useSelector((state: any) => state.setReducer);
  const openRoute = useOpenRoute();
  const routeLink = useRouteLink();
  const shortcuts = useHomeShortcuts();
  const { isDesktop, isMobile } = useResponsive();
  const { setIsMobileMenuOpen } = useMobileMenu();
  const { refreshPermissions } = usePermissions();

  const { t, i18n } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const [isRefreshingPermissions, setIsRefreshingPermissions] = useState(false);

  const [isFullScreen, setIsFullScreen] = useState(false);

  // Customer search state
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<any>(null);
  const currentSearchKeywordRef = useRef<string>("");

  function handleLogout() {
    // Mark logout as in-progress BEFORE anything else so the Network interceptor and
    // getToken guards suppress every toast ("session expired", "No token found", etc.).
    localStorage.setItem("isLoggingOut", "true");
    localStorage.setItem("silentLogout", "true");

    const companyId = userInfo?.company_instance?._id;

    // Fire-and-forget: don't await the logout call. If the token is already expired we don't
    // want the 401 to race with our local cleanup and we don't want to block navigation.
    Api.post("auth/logout", { company: companyId }).catch(() => {
      // Ignore — local logout always proceeds.
    });

    // Cancel every pending request so their completion handlers don't fire alerts.
    try {
      requestManager.cancelAllRequests();
    } catch {
      // noop
    }

    // Local cleanup (localStorage only). Do NOT dispatch setUserInfo(null) or touch any React
    // state here — that causes PrivateRoute's useEffect to emit a <Redirect to="/login" />
    // on the same tick we do window.location.href, producing a visible double-navigation.
    // window.location.href below is a full page load and wipes Redux/React state cleanly.
    localStorage.removeItem("token");
    localStorage.removeItem("socketUrl");
    localStorage.removeItem("refresh-token");
    localStorage.removeItem("awfar_user_permissions");
    deleteQueryPresister();

    window.location.href = "/login";
  }

  const handleRefreshPermissions = async () => {
    setIsRefreshingPermissions(true);
    try {
      await refreshPermissions();
      message.success(t("permissions.refreshSuccess", "Permissions refreshed successfully"));
    } catch (error) {
      message.error(t("permissions.refreshError", "Failed to refresh permissions"));
    } finally {
      setIsRefreshingPermissions(false);
    }
  };

  // Get initial theme from localStorage or default to "lightTheme"

  function handleFullScreen() {
    if (!isFullScreen) return document.body.requestFullscreen();
    return document.exitFullscreen();
  }

  useEffect(() => {
    function onFullscreenChange() {
      setIsFullScreen(Boolean(document.fullscreenElement));
    }
    document.addEventListener("fullscreenchange", onFullscreenChange);

    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Focus input when search expands
  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      // Small delay to ensure the input is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchExpanded]);

  // Close search when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchExpanded(false);
        setCustomers([]);
        setIsDropdownOpen(false);
        setSearchInputValue("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Customer search function
  const searchCustomers = async (keyword: string) => {
    if (keyword.trim().length < 2) {
      setCustomers([]);
      setIsSearching(false);
      setIsDropdownOpen(false);
      return;
    }

    currentSearchKeywordRef.current = keyword;
    setIsSearching(true);
    setIsDropdownOpen(true);

    try {
      const response = await Api.post(`customers/search`, { keyword });

      if (currentSearchKeywordRef.current === keyword) {
        const customerData = response.data || response;
        const customersArray = Array.isArray(customerData) ? customerData : [];
        setCustomers(customersArray);
        setIsDropdownOpen(customersArray.length > 0);
      }
    } catch (error) {
      console.error("Customer search error:", error);
      setCustomers([]);
      setIsDropdownOpen(false);
    } finally {
      if (currentSearchKeywordRef.current === keyword) {
        setIsSearching(false);
      }
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (keyword: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          searchCustomers(keyword);
        }, 400);
      };
    })(),
    []
  );

  const handleSearch = (value: string) => {
    setSearchInputValue(value);
    currentSearchKeywordRef.current = value;
    if (value.trim().length >= 2) {
      setIsDropdownOpen(true);
      debouncedSearch(value);
    } else {
      setCustomers([]);
      setIsSearching(false);
      setIsDropdownOpen(false);
    }
  };

  const navigateToCustomerDetails = (customerId: number, event: React.MouseEvent) => {
    console.log("navigateToCustomerDetails called with:", customerId);
    openRoute(`/customers/${customerId}/details`, event);
    // A new-tab click leaves the results open so more customers can be opened.
    if (isNewTabClick(event)) return;
    setIsSearchExpanded(false);
    setCustomers([]);
    setIsDropdownOpen(false);
    setSearchInputValue("");
  };


  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "logout",
      onClick: () => {
        handleLogout();
      },
      label: (
        <>
          <span className="icns-drop-header">
            <LogoutIcon />
          </span>
          {t("header.Log_out", "Log out")}
        </>
      ),
    },
    {
      key: "refreshPermissions",
      onClick: handleRefreshPermissions,
      disabled: isRefreshingPermissions,
      label: (
        <>
          <span className="icns-drop-header">
            <ReloadOutlined spin={isRefreshingPermissions} />
          </span>
          {isRefreshingPermissions
            ? t("permissions.refreshing", "Refreshing...")
            : t("permissions.refreshPermissions", "Refresh Permissions")}
        </>
      ),
    },
  ];

  const profileName = userInfo?.name || userInfo?.email || "User";
  // "Farah admin" → "FA"; a one-word name or an email gives one letter.
  const profileInitials =
    profileName
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part.charAt(0))
      .join("")
      .toUpperCase() || "U";

  return (
    <header className="app-header">
      <div className="logos-container">
        {isMobile && (
          <button
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              marginRight: '10px'
            }}
          >
            <MenuOutlined />
          </button>
        )}
        {/* On desktop the logos live at the top of the sidebar (theme
            `.logorow`); a phone has no sidebar, so they stay here. */}
        {isMobile && (
          <>
            <img
              src={isDark ? "/assets/awfarLogo.webp" : "/assets/awfarLogoColor.webp"}
              alt="Awfar"
            />
            <span className="header-logos-border">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1"
                height="38"
                viewBox="0 0 1 38"
                fill="none"
              >
                <line
                  x1="0.5"
                  y1="2.18557e-08"
                  x2="0.499998"
                  y2="38"
                  stroke="white"
                  strokeOpacity="0.2"
                />
              </svg>
            </span>
            {(() => {
              const companyInfo = company?.company_information;
              const isArabic = i18n.language === "ar";
              const logoUrl = isArabic ? companyInfo?.logo_ar : companyInfo?.logo;
              return logoUrl ? (
                <ImageWithChecker
                  imageUrl={logoUrl}
                  defaultIcon="/assets/logoColored.webp"
                  useSrc={true}
                />
              ) : (
                <div className="no-logo-placeholder">
                  <div>
                    {companyInfo?.name_ar || companyInfo?.name_en || "Company"}
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* Customer Search */}
        <div 
          ref={searchContainerRef}
          className={`header-customer-search ${isSearchExpanded ? 'expanded' : ''}`}
          onClick={!isSearchExpanded ? handleSearchIconClick : undefined}
        >
          <div 
            className="search-icon-wrapper"
          >
            <SearchOutlined />
          </div>
          
          <div className="search-input-wrapper" onClick={(e) => e.stopPropagation()}>
            <Input
              ref={searchInputRef}
              placeholder={t("header.searchCustomer", "Search customer...")}
              prefix={<SearchOutlined />}
              value={searchInputValue}
              onChange={(e) => {
                const value = e.target.value;
                handleSearch(value);
              }}
              onFocus={() => {
                if (customers.length > 0) {
                  setIsDropdownOpen(true);
                }
              }}
              className="header-customer-input"
              allowClear
            />
            {isDropdownOpen && customers.length > 0 && (
              <div className="header-customer-dropdown-custom">
                {isSearching ? (
                  <div className="dropdown-loading">
                    <Spin size="small" />
                  </div>
                ) : customers.length === 0 ? (
                  <div className="dropdown-empty">
                    {t("header.noCustomersFound", "No customers found")}
                  </div>
                ) : (
                  <div className="dropdown-list">
                    {customers.map((customer) => (
                      <div
                        key={customer.id}
                        className="customer-dropdown-item"
                        onClick={(event) => {
                          console.log("Customer item clicked:", customer.id);
                          navigateToCustomerDetails(customer.id, event);
                        }}
                      >
                        <div className="customer-option">
                          <AntAvatar size="small" icon={<UserIcon width={14} height={14} />} />
                          <div className="customer-info">
                            <div className="customer-name">{customer.name}</div>
                            {customer.phone && (
                              <div className="customer-phone">{customer.phone}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* The Home card's quick links, always one click away */}
      {shortcuts.length > 0 && (
        <nav className="header-links" aria-label={t("header.shortcuts", "Shortcuts")}>
          {shortcuts.map((shortcut) => (
            <Tooltip key={shortcut.key} title={shortcut.label}>
              <a {...routeLink(shortcut.to)} aria-label={shortcut.label}>
                {shortcut.icon}
              </a>
            </Tooltip>
          ))}
        </nav>
      )}

      <div className="header-controls">
        {/* Tool icons share one glass group (the profile pill stays apart) */}
        <div className="header-tools">
          {/* Chatbot: opens the chat system in a new tab (as in the old UI) */}
          <div className="chatbot-link">
            <a
              href={CHATBOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              title={t("header.chatbot", "Chatbot")}
              aria-label={t("header.chatbot", "Chatbot")}
            >
              <svg
                aria-hidden="true"
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v3M8 6h8a4 4 0 0 1 4 4v5a4 4 0 0 1-4 4h-5l-4 3v-3a4 4 0 0 1-3-3.87V10a4 4 0 0 1 4-4M9.5 12v1M14.5 12v1" />
              </svg>
            </a>
          </div>

          <div className="theme-toggle">
            <button
              type="button"
              onClick={toggleTheme}
              aria-pressed={isDark}
              title={
                isDark
                  ? t("header.lightMode", "Light mode")
                  : t("header.darkMode", "Dark mode")
              }
              aria-label={
                isDark
                  ? t("header.lightMode", "Light mode")
                  : t("header.darkMode", "Dark mode")
              }
            >
              {isDark ? <DarkMode /> : <LightMode />}
            </button>
          </div>

          {/* Theme `.iconbtn` globe: one click switches to the other language */}
          <div className="language-toggle">
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar")}
              title={i18n.language === "ar" ? "English" : "العربية"}
              aria-label={i18n.language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
            >
              <svg
                aria-hidden="true"
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0M3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 0 0 0 18M12.5 3a17 17 0 0 1 0 18" />
              </svg>
            </button>
          </div>

          <div className="fullscreen-toggle">
            <button onClick={handleFullScreen}>
              <FullScreenIcon />
            </button>
          </div>

          <div className="notification-center">
            <NotificationCenter />
          </div>
        </div>

        <div className="profile-icon">
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="header-account-menu"
            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
          >
            <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
              {/* Theme `.ubtn`: gradient initials disc, name, chevron */}
              <span className="profile-initials" aria-hidden="true">
                {profileInitials}
              </span>
              <span className="ptofile-text">{profileName}</span>
              <svg
                className="profile-chevron"
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9l6 6l6-6" />
              </svg>
            </a>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
